import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import { z } from 'zod';

import { Box, Button, Heading, Paragraph, Text } from '@rover/kibble/core';
import { Form, useKibbleForm } from '@rover/kibble/forms';
import TextField from '@rover/kibble/forms/fields/TextField';
import Banner from '@rover/kibble/official-patterns/Banner';
import LocationInputContainer from '@rover/react-lib/src/containers/LocationInputContainer';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import {
  type SearchNameCardClickTarget,
  resolveLocationMethod,
  SearchNameCardClickedEvent,
  SearchNameLocationChangedEvent,
} from '../analyticsEvents';
import SearchByNameSitterCard from '../components/SearchByNameSitterCard';
import { MAX_NAME_LENGTH, MIN_NAME_LENGTH } from '../constants';
import type { SearchParams } from '../hooks/useSearchByName';
import type { NameSearchResult } from '../types';
import { type LocationResolution, classifyCoords, resolveLocation } from '../utils/resolveLocation';

const NAME_FIELD = 'sitterName';
const LOCATION_INPUT_ID = 'search-by-name-location';

type RejectReason = Extract<LocationResolution, { ok: false }>['reason'];
const locationErrorMessage = (reason: RejectReason): string =>
  reason === 'too_broad'
    ? t`Please enter a more specific location, like a city, street address, or zip code.`
    : t`We couldn't find that location. Try a city, street address, or zip code.`;

type Props = {
  initialLocation?: string;
  results: NameSearchResult[] | null;
  query: string;
  isLoading: boolean;
  error: string | null;
  searchUuid: string | null;

  onSearch: (params: SearchParams) => void;
};

const ModalSearchScreen = ({
  initialLocation = '',
  results,
  query,
  isLoading,
  error,
  searchUuid,
  onSearch,
}: Props): JSX.Element => {
  const headingId = useId();
  const { setScreenReaderAnnouncement } = useA11yContext();
  const [location, setLocation] = useState(initialLocation);
  const [lat, setLat] = useState<string | null>(null);
  const [lng, setLng] = useState<string | null>(null);
  const [zip, setZip] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);
  const resultsRef = useRef(results);
  useEffect(() => {
    resultsRef.current = results;
  }, [results]);
  const nameInputRef = useRef<HTMLElement | null>(null);

  const { configureField, formProps, watch } = useKibbleForm({
    schema: z.object({
      sitterName: z.string().trim().min(MIN_NAME_LENGTH, 'name-too-short'),
    }),
    defaultValues: { sitterName: '' },
    onValidSubmit: async (data) => {
      if (isLoading || isResolvingLocation) return;

      const trimmedLocation = location.trim();
      if (!trimmedLocation) {
        const message = t`Please enter a location`;
        setLocationError(message);
        setScreenReaderAnnouncement(message);
        // LocationInputContainer doesn't forward a ref; focus by id.
        // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
        document.getElementById(LOCATION_INPUT_ID)?.focus();
        return;
      }

      let resolvedLat = lat;
      let resolvedLng = lng;
      let resolvedZip = zip;
      if (!resolvedLat || !resolvedLng) {
        setIsResolvingLocation(true);
        const resolution = await resolveLocation(trimmedLocation);
        setIsResolvingLocation(false);
        if (!resolution.ok) {
          const message = locationErrorMessage(resolution.reason);
          setLocationError(message);
          setScreenReaderAnnouncement(message);
          // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
          document.getElementById(LOCATION_INPUT_ID)?.focus();
          return;
        }
        resolvedLat = resolution.lat;
        resolvedLng = resolution.lng;
        resolvedZip = resolution.postalCode;
        setLat(resolvedLat);
        setLng(resolvedLng);
        setZip(resolvedZip);
        // Typed-then-submitted locations skip handleLocationChange, so we should also emit here.
        emitAnalyticsEvent(
          new SearchNameLocationChangedEvent({
            locationMethod: resolveLocationMethod(resolution.locationType),
            coordsResolved: true,
            hadResultsBefore: resultsRef.current !== null,
          })
        );
      }

      // Clamp so a pathological query can't be interpolated verbatim into UI text.
      const clampedName = data.sitterName.slice(0, MAX_NAME_LENGTH);
      onSearch({
        name: clampedName,
        lat: resolvedLat,
        lng: resolvedLng,
        location: trimmedLocation,
        locationZip: resolvedZip,
      });
    },
  });

  const nameFieldConfig = configureField(NAME_FIELD);
  const nameFieldErrorMessage = nameFieldConfig.fieldError
    ? t`Please enter at least ${MIN_NAME_LENGTH} characters of the sitter's name`
    : undefined;

  const handleLocationChange = useCallback(
    (
      data: {
        value?: string | null;
        lat?: string | null;
        lng?: string | null;
        locationType?: string | null;
        postalCode?: string | null;
      } | null
    ) => {
      const val = data?.value ?? '';
      setLocation(val);
      if (!val.trim()) {
        setLat(null);
        setLng(null);
        setZip(null);
        return;
      }
      const resolution = classifyCoords(data);
      emitAnalyticsEvent(
        new SearchNameLocationChangedEvent({
          locationMethod: resolveLocationMethod(data?.locationType),
          coordsResolved: resolution.ok,
          hadResultsBefore: resultsRef.current !== null,
        })
      );
      if (!resolution.ok) {
        setLat(null);
        setLng(null);
        setZip(null);
        setLocationError(locationErrorMessage(resolution.reason));
        return;
      }
      setLat(resolution.lat);
      setLng(resolution.lng);
      setZip(resolution.postalCode);
      setLocationError(null);
    },
    []
  );

  // LocationInput quirk: typing fires onInputChange, not onChange.
  const handleLocationInputChange = useCallback((value: string) => {
    setLocation(value);
    setLat(null);
    setLng(null);
    setZip(null);
    setLocationError(null);
  }, []);

  // Separate flag from isResolvingLocation so a submit-triggered blur can't block its own geocode.
  const isBlurResolvingRef = useRef(false);
  const handleLocationBlur = useCallback(async () => {
    if (isBlurResolvingRef.current) return;
    const trimmedLocation = location.trim();
    if (!trimmedLocation || (lat && lng)) return;

    isBlurResolvingRef.current = true;
    const resolution = await resolveLocation(trimmedLocation);
    isBlurResolvingRef.current = false;
    // Bail if the user kept editing while the geocode was in flight.
    if (locationRef.current.trim() !== trimmedLocation) return;
    if (!resolution.ok) {
      const message = locationErrorMessage(resolution.reason);
      setLocationError(message);
      setScreenReaderAnnouncement(message);
      return;
    }
    setLat(resolution.lat);
    setLng(resolution.lng);
    setZip(resolution.postalCode);
    setLocationError(null);
  }, [location, lat, lng, setScreenReaderAnnouncement]);

  // Pre-warm coords for the seeded location; discard if the user has since edited.
  useEffect(() => {
    const seed = initialLocation.trim();
    if (!seed) return undefined;

    let cancelled = false;
    resolveLocation(seed)
      .then((resolution) => {
        if (cancelled || locationRef.current.trim() !== seed) return undefined;
        if (resolution.ok) {
          setLat(resolution.lat);
          setLng(resolution.lng);
          setZip(resolution.postalCode);
        }
        return undefined;
      })
      .catch(() => undefined);

    return (): void => {
      cancelled = true;
    };
  }, [initialLocation]);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  // Focus the results heading so SR + keyboard users land in the result region.
  useEffect(() => {
    if (results === null || isLoading || error) return;
    // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
    const heading = document.getElementById(headingId);
    heading?.focus();
  }, [results, isLoading, error, headingId]);

  const hasSearched = results !== null;
  const resultCount = results?.length ?? 0;
  const hasResults = resultCount > 0;
  const isSubmitting = isLoading || isResolvingLocation;
  const nameValue = watch(NAME_FIELD) ?? '';
  const isFormIncomplete =
    nameValue.trim().length < MIN_NAME_LENGTH ||
    location.trim().length === 0 ||
    locationError !== null;

  const handleCardClick = useCallback(
    (result: NameSearchResult, index: number, clickTarget: SearchNameCardClickTarget): void => {
      if (!searchUuid) return;
      emitAnalyticsEvent(
        new SearchNameCardClickedEvent({
          rank: index + 1,
          sitterPersonOpk: result.personOpk,
          resultCount,
          searchUuid,
          isPreviouslyBooked: result.isPreviouslyBooked,
          isStarSitter: result.isStarSitter,
          clickTarget,
        })
      );
    },
    [searchUuid, resultCount]
  );

  return (
    <Box testID="search-by-name-screen">
      <Form {...formProps} hideRequiredFieldWarning>
        <Box pb="8x">
          <TextField
            {...nameFieldConfig}
            translatedFieldErrorMessage={nameFieldErrorMessage}
            primaryLabel={t`Sitter name`}
            placeholder={t`Enter sitter's name`}
            hideRequiredAsterisk
            marginBottom="4x"
            webInputRef={(el): void => {
              nameInputRef.current = el;
            }}
          />
          <Text
            as="label"
            fontWeight="semibold"
            display="block"
            webProps={{ htmlFor: LOCATION_INPUT_ID }}
          >
            {t`Location`}
          </Text>
          <LocationInputContainer
            inputId={LOCATION_INPUT_ID}
            inputHeight="48px"
            inputValue={location}
            onChange={handleLocationChange}
            onInputChange={handleLocationInputChange}
            onBlur={handleLocationBlur}
            placeholder={t`City, street address, or zip code`}
            validationType="inline"
            validationError={locationError}
            channel="search-by-name"
            enableLatLng
          />
        </Box>
        <Button
          variant="primary"
          fullWidth
          webProps={{ type: 'submit' }}
          loading={isSubmitting}
          disabled={isSubmitting || isFormIncomplete}
          testID="search-by-name-submit"
        >
          {t`Search`}
        </Button>
      </Form>

      {error && (
        <Box>
          <Banner severity="danger" testID="search-by-name-error">
            {error}
          </Banner>
        </Box>
      )}

      {hasSearched && !error && (
        <>
          {/* Heading's webProps doesn't accept tabIndex; hang the focus target on the wrapping Box.
              aria-labelledby still resolves to the heading text via the Box's accessible name. */}
          <Box webProps={{ id: headingId, tabIndex: -1 }} m="0x" p="0x" mt="4x">
            <Heading size="300" testID="search-by-name-results-heading">
              {query ? t`Results for ${query}` : t`Results`}
            </Heading>
          </Box>

          <Box>
            {!isLoading &&
              (!hasResults ? (
                <Box
                  textAlign="center"
                  testID="search-by-name-empty-results"
                  webAs="section"
                  webProps={{ 'aria-labelledby': headingId }}
                >
                  <Paragraph size="200" textColor="secondary">
                    {t`No sitters found matching ${query}.`}
                  </Paragraph>
                  <Text size="100" textColor="tertiary" mt="2x" mb="4x" display="block">
                    {t`Try a different spelling or location.`}
                  </Text>
                </Box>
              ) : (
                <Box
                  webAs="ul"
                  webProps={{ 'aria-labelledby': headingId }}
                  p="4x"
                  m="0x"
                  sx={{ listStyle: 'none' }}
                >
                  {results?.map((result, index) => (
                    <Box webAs="li" key={`${searchUuid}-${result.personOpk}`} py="2x">
                      <SearchByNameSitterCard
                        result={result}
                        rank={index + 1}
                        resultCount={resultCount}
                        searchUuid={searchUuid}
                        onCardClick={(target) => handleCardClick(result, index, target)}
                      />
                    </Box>
                  ))}
                </Box>
              ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ModalSearchScreen;
